import { useCallback, useEffect, useRef, useState } from 'react';
import { HEARTBEAT_MS } from './constants';
import { useAuth } from '@/store/auth';
import { workApi } from '@/services/hrm';

const INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'] as const;

/**
 * Working-session monitor (employees only).
 *
 * Privacy: we record only *that* an interaction happened (a timestamp). Key
 * values, pointer positions, page content and URLs are never captured or sent.
 */
export function useWorkSessionMonitor() {
  const tracks = useAuth((s) => !!s.user?.tracksWorkTime);
  const policy = useAuth((s) => s.tenant?.sessionPolicy);
  const state = useAuth((s) => s.workSession?.state);
  const setWorkSession = useAuth((s) => s.setWorkSession);
  const lastInteraction = useRef(Date.now());
  const [idleFor, setIdleFor] = useState(0);
  const sentTimeoutBeat = useRef(false);

  const beat = useCallback(async () => {
    if (!tracks) return;
    try {
      const r = await workApi.heartbeat({ idleForMs: Math.max(0, Date.now() - lastInteraction.current), tabVisible: !document.hidden });
      if (r.session) setWorkSession(r.session);
    } catch {
      /* 401 handling (idle timeout / revoked) is done centrally by the API client */
    }
  }, [tracks, setWorkSession]);

  useEffect(() => {
    if (!tracks) return;
    const mark = () => {
      lastInteraction.current = Date.now();
      sentTimeoutBeat.current = false;
    };
    for (const e of INTERACTION_EVENTS) window.addEventListener(e, mark, { passive: true, capture: true });
    const onVisible = () => document.visibilityState === 'visible' && void beat();
    document.addEventListener('visibilitychange', onVisible);
    const hb = window.setInterval(() => void beat(), HEARTBEAT_MS);
    const tick = window.setInterval(() => setIdleFor(Date.now() - lastInteraction.current), 1000);
    void beat();
    return () => {
      for (const e of INTERACTION_EVENTS) window.removeEventListener(e, mark, { capture: true });
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(hb);
      window.clearInterval(tick);
    };
  }, [tracks, beat]);

  const timeoutMs = (policy?.idleTimeoutMinutes ?? 5) * 60_000;
  const warnMs = timeoutMs - (policy?.idleWarningSeconds ?? 60) * 1000;
  const enforce = tracks && !!policy?.enforceIdleLogout && state !== 'on_break';
  const showWarning = enforce && idleFor >= warnMs;

  // At the limit, report to the server immediately; it ends the session and the client follows.
  useEffect(() => {
    if (enforce && idleFor >= timeoutMs && !sentTimeoutBeat.current) {
      sentTimeoutBeat.current = true;
      void beat();
    }
  }, [enforce, idleFor, timeoutMs, beat]);

  const stayActive = useCallback(() => {
    lastInteraction.current = Date.now();
    setIdleFor(0);
    void beat();
  }, [beat]);

  return { tracks, idleFor, showWarning, secondsLeft: Math.max(0, Math.ceil((timeoutMs - idleFor) / 1000)), stayActive };
}
